<template>
  <Teleport to="#vcpopovercontainer">
    <template v-if="show">
      <div class="modal-backdrop fade show" @click="$emit('close')"></div>
      <div class="modal fade show d-block" tabindex="-1" style="z-index: 1060;">
        <div class="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable" style="position: relative; z-index: 1061;">
        <div class="modal-content">
          <!-- Header -->
          <div class="modal-header py-2">
            <h6 class="modal-title mb-0">
              <i class="bi bi-share me-1"></i>Share: {{ definitionName || 'Untitled' }}
            </h6>
            <button type="button" class="btn-close btn-close-sm" @click="$emit('close')"></button>
          </div>

          <!-- Body -->
          <div class="modal-body py-3" style="min-height: 300px;">
            <!-- Loading -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
              <div class="text-muted mt-2 small">Loading shares…</div>
            </div>

            <!-- Error -->
            <div v-else-if="error" class="alert alert-danger small">
              <i class="bi bi-exclamation-triangle me-1"></i>{{ error }}
              <button type="button" class="btn btn-sm btn-outline-danger ms-2" @click="loadShares">Retry</button>
            </div>

            <template v-else>
              <!-- Share saved success panel -->
              <div v-if="createdShareInfo" class="text-center py-4">
                <div class="mb-3">
                  <i class="bi bi-check-circle-fill text-success" style="font-size: 2.5rem;"></i>
                </div>
                <h6 class="mb-3">{{ createdShareInfo._wasEdit ? 'Share Updated Successfully' : 'Share Created Successfully' }}</h6>
                <p class="text-muted small mb-3">Your share link is ready. Copy it and send to anyone you want to share with.</p>
                <div class="input-group mx-auto" style="max-width: 500px;">
                  <input type="text" class="form-control form-control-sm bg-white" :value="createdShareLink" readonly @focus="$event.target.select()" ref="shareLinkInput">
                  <button class="btn btn-sm" :class="copiedShareId === createdShareInfo.shareId ? 'btn-success' : 'btn-primary'" @click="copyCreatedShareLink" style="min-width: 90px;">
                    <i class="bi me-1" :class="copiedShareId === createdShareInfo.shareId ? 'bi-check-lg' : 'bi-clipboard'"></i>
                    {{ copiedShareId === createdShareInfo.shareId ? 'Copied!' : 'Copy Link' }}
                  </button>
                </div>

                <!-- Email notification option -->
                <div v-if="createdShareInfo._allowedUsers && createdShareInfo._allowedUsers.length" class="mt-3 mx-auto text-start border rounded p-3 bg-light" style="max-width: 500px;">
                  <div class="form-check mb-2">
                    <input v-model="sendEmailNotification" class="form-check-input" type="checkbox" id="sendEmailCheck">
                    <label class="form-check-label small" for="sendEmailCheck">
                      <i class="bi bi-envelope me-1"></i>Send email notification to shared users
                    </label>
                  </div>
                  <div v-if="sendEmailNotification" class="small text-muted mb-2 ms-4">
                    <span v-for="(userId, idx) in createdShareInfo._allowedUsers" :key="userId">
                      {{ resolveUserName(userId) }}<span v-if="idx < createdShareInfo._allowedUsers.length - 1">, </span>
                    </span>
                  </div>
                  <div v-if="emailSent" class="small text-success ms-4">
                    <i class="bi bi-check-circle me-1"></i>{{ emailSentMessage }}
                  </div>
                  <div v-if="emailError" class="small text-danger ms-4">
                    <i class="bi bi-exclamation-circle me-1"></i>{{ emailError }}
                  </div>
                  <div v-if="sendEmailNotification && !emailSent" class="ms-4">
                    <button class="btn btn-sm btn-outline-primary" @click="sendNotificationEmails" :disabled="emailSending">
                      <span v-if="emailSending" class="spinner-border spinner-border-sm me-1"></span>
                      <i v-else class="bi bi-send me-1"></i>Send Emails
                    </button>
                  </div>
                </div>

                <div class="mt-3">
                  <button class="btn btn-outline-secondary btn-sm" @click="dismissCreatedShare">Done</button>
                </div>
              </div>

              <!-- Existing shares list -->
              <div v-if="shares.length > 0 && !createdShareInfo && !showCreateForm" class="mb-3">
                <h6 class="small text-muted text-uppercase mb-2">Active Shares ({{ shares.length }}/{{ maxSharesPerDefinition }})</h6>
                <div class="list-group list-group-flush">
                  <div v-for="share in sortedShares" :key="share.shareId" class="list-group-item px-0">
                    <div class="d-flex align-items-start">
                      <div class="flex-grow-1">
                        <div class="d-flex align-items-center gap-2 mb-1">
                          <strong class="small">{{ share.name || 'Unnamed share' }}</strong>
                          <span v-if="!share.enabled" class="badge bg-secondary" style="font-size: 0.65rem;">Disabled</span>
                          <span v-if="share.passwordHash" class="badge bg-info text-dark" style="font-size: 0.65rem;">
                            <i class="bi bi-lock-fill me-1"></i>Password
                          </span>
                          <span v-if="share.allowedUsers && share.allowedUsers.length" class="badge bg-primary" style="font-size: 0.65rem;">
                            <i class="bi bi-people-fill me-1"></i>{{ share.allowedUsers.length }} user{{ share.allowedUsers.length > 1 ? 's' : '' }}
                          </span>
                          <span v-if="share.notifyOnUpdate" class="badge bg-success" style="font-size: 0.65rem;">
                            <i class="bi bi-envelope-fill me-1"></i>Notify
                          </span>
                        </div>
                        <div class="d-flex align-items-center gap-3 text-muted" style="font-size: 0.72rem;">
                          <span><i class="bi bi-eye me-1"></i>{{ share.accessCount || 0 }} views</span>
                          <span v-if="share.expiresAt">
                            <i class="bi bi-clock me-1"></i>Expires: {{ formatDate(share.expiresAt) }}
                            <span v-if="isExpired(share)" class="text-danger">(expired)</span>
                          </span>
                          <span>Created: {{ formatDate(share.createdAt) }}</span>
                        </div>
                      </div>
                      <div class="d-flex align-items-center gap-1 ms-2">
                        <button class="btn btn-sm btn-outline-primary" @click="copyShareLink(share)" :title="copiedShareId === share.shareId ? 'Copied!' : 'Copy link'">
                          <i class="bi" :class="copiedShareId === share.shareId ? 'bi-check-lg' : 'bi-clipboard'"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="showAnalytics(share)" title="View analytics">
                          <i class="bi bi-bar-chart"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="startEditShare(share)" title="Edit">
                          <i class="bi bi-pencil"></i>
                        </button>
                        <button
                          v-if="share.notifyOnUpdate && share.allowedUsers && share.allowedUsers.length"
                          class="btn btn-sm btn-outline-success"
                          @click="sendShareUpdateEmail(share)"
                          :disabled="sendingUpdateShareId === share.shareId"
                          title="Send data-updated email to this share's users"
                        >
                          <span v-if="sendingUpdateShareId === share.shareId" class="spinner-border spinner-border-sm"></span>
                          <i v-else class="bi bi-envelope"></i>
                        </button>
                        <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteShare(share)" title="Delete">
                          <i class="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>
                    <!-- Inline delete confirmation -->
                    <div v-if="deleteConfirmShare && deleteConfirmShare.shareId === share.shareId" class="mt-2">
                      <div class="alert alert-warning small d-flex align-items-center mb-0 py-2">
                        <i class="bi bi-exclamation-triangle me-2"></i>
                        <span class="flex-grow-1">Delete this share? This cannot be undone.</span>
                        <button class="btn btn-sm btn-danger me-1" @click="doDeleteShare" :disabled="deleteInProgress">
                          <span v-if="deleteInProgress" class="spinner-border spinner-border-sm me-1"></span>Delete
                        </button>
                        <button class="btn btn-sm btn-outline-secondary" @click="deleteConfirmShare = null">Cancel</button>
                      </div>
                    </div>
                    <!-- Email send feedback -->
                    <div v-if="updateEmailShareMessage && updateEmailShareMessage.shareId === share.shareId" class="mt-1">
                      <small :class="updateEmailShareMessage.error ? 'text-danger' : 'text-success'">
                        <i class="bi me-1" :class="updateEmailShareMessage.error ? 'bi-exclamation-circle' : 'bi-check-circle'"></i>{{ updateEmailShareMessage.text }}
                      </small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- No shares -->
              <div v-else-if="!showCreateForm && !createdShareInfo" class="text-center py-4">
                <i class="bi bi-share text-muted" style="font-size: 2rem;"></i>
                <p class="text-muted small mt-2 mb-0">No shares yet. Create one to allow others to view this tracker's results.</p>
              </div>

              <!-- Create / Edit form -->
              <div v-if="showCreateForm" class="share-form-container mt-2">
                <div class="share-form-header">
                  <h6 class="fw-semibold mb-0">{{ editingShare ? 'Edit Share' : 'Create New Share' }}</h6>
                </div>

                <div class="share-form-body">
                  <!-- Share Name -->
                  <div class="mb-3">
                    <label class="form-label small fw-medium mb-1">Share Name</label>
                    <input v-model="form.name" type="text" class="form-control form-control-sm"
                      placeholder="e.g. Client Dashboard View" maxlength="200" autocomplete="off">
                  </div>

                  <!-- Sharing approach selector -->
                  <div class="mb-1">
                    <label class="form-label small fw-medium mb-2">Sharing Method</label>
                  </div>

                  <div class="share-approach-options mb-3">
                    <!-- Option 1: Share with account users -->
                    <label class="share-approach-card" :class="{ 'share-approach-card--active': form.shareApproach === 'users' }">
                      <div class="d-flex align-items-start gap-2">
                        <input v-model="form.shareApproach" type="radio" value="users" class="form-check-input mt-1">
                        <div>
                          <div class="fw-medium small"><i class="bi bi-people-fill me-1 text-primary"></i>Share with Account Users</div>
                          <div class="text-muted" style="font-size: 0.75rem;">Recipients must log in to their account to access this share.</div>
                        </div>
                      </div>
                    </label>

                    <!-- Option 2: Open link -->
                    <label class="share-approach-card" :class="{ 'share-approach-card--active': form.shareApproach === 'link' }">
                      <div class="d-flex align-items-start gap-2">
                        <input v-model="form.shareApproach" type="radio" value="link" class="form-check-input mt-1">
                        <div>
                          <div class="fw-medium small"><i class="bi bi-link-45deg me-1 text-success"></i>Share via Open Link</div>
                          <div class="text-muted" style="font-size: 0.75rem;">Anyone with the link can access. Optionally protect with a password.</div>
                        </div>
                      </div>
                    </label>
                  </div>

                  <!-- Section: Account Users details -->
                  <div v-if="form.shareApproach === 'users'" class="share-section mb-3">
                    <div class="share-section-header">
                      <i class="bi bi-people me-1"></i>Select Users
                      <span v-if="form.allowedUsers.length" class="badge bg-primary ms-1" style="font-size: 0.65rem;">{{ form.allowedUsers.length }}</span>
                    </div>
                    <div class="share-section-body">
                      <!-- Add User -->
                      <div v-if="availableChildUsers.length" class="mb-2">
                        <div class="input-group input-group-sm">
                          <select v-model="selectedChildUser" class="form-select form-select-sm">
                            <option value="">Select a user to add…</option>
                            <option v-for="u in availableChildUsers" :key="u.id" :value="u.id">{{ u.userName }}</option>
                          </select>
                          <button class="btn btn-primary" @click="addSelectedUser" :disabled="!selectedChildUser">
                            <i class="bi bi-plus-lg"></i> Add
                          </button>
                        </div>
                      </div>

                      <!-- User list -->
                      <div v-for="(userId, idx) in form.allowedUsers" :key="idx" class="d-flex align-items-center gap-1 mb-1" style="max-width: 350px;">
                        <span class="form-control form-control-sm bg-white" style="cursor: default;">{{ resolveUserName(userId) }}</span>
                        <button class="btn btn-outline-danger btn-sm" @click="form.allowedUsers.splice(idx, 1)" title="Remove">
                          <i class="bi bi-x"></i>
                        </button>
                      </div>
                      <div v-if="childUsersLoading" class="small text-muted"><span class="spinner-border spinner-border-sm me-1"></span>Loading users…</div>
                      <div v-else-if="childUsers.length && !availableChildUsers.length && form.allowedUsers.length" class="small text-muted">All users added.</div>
                      <div v-else-if="childUsersError" class="small text-danger">{{ childUsersError }}</div>
                      <div v-if="!form.allowedUsers.length && !childUsersLoading" class="small text-muted fst-italic">No users selected yet. Add at least one user.</div>
                    </div>

                    <!-- Notify on data update -->
                    <div class="mt-2 pt-2 border-top">
                      <div class="form-check">
                        <input v-model="form.notifyOnUpdate" class="form-check-input" type="checkbox" id="notifyOnUpdateCheck">
                        <label class="form-check-label small" for="notifyOnUpdateCheck">
                          <i class="bi bi-envelope me-1"></i>Email users when data is updated
                        </label>
                      </div>
                      <div class="text-muted mt-1" style="font-size: 0.7rem;">Selected users will receive an email when scheduled data refreshes complete (max once per day).</div>
                    </div>
                  </div>

                  <!-- Section: Open link details -->
                  <div v-if="form.shareApproach === 'link'" class="share-section mb-3">
                    <div class="share-section-header">
                      <i class="bi bi-lock me-1"></i>Password Protection
                    </div>
                    <div class="share-section-body">
                      <div class="d-flex align-items-center gap-2">
                        <input v-model="form.password" type="text" class="form-control form-control-sm fwt-password-input"
                          :placeholder="editingShare && editingShare.passwordHash ? 'Enter new password to change' : 'Optional password'"
                          autocomplete="off" style="max-width: 300px;">
                        <div v-if="editingShare && editingShare.passwordHash" class="form-check">
                          <input v-model="form.clearPassword" class="form-check-input" type="checkbox" id="clearPasswordCheck">
                          <label class="form-check-label small" for="clearPasswordCheck">Remove</label>
                        </div>
                      </div>
                      <div class="text-muted mt-1" style="font-size: 0.75rem;">Leave blank for unrestricted access via the link.</div>
                    </div>
                  </div>

                  <!-- Common: Expiration -->
                  <div class="share-section mb-3">
                    <div class="share-section-header">
                      <i class="bi bi-calendar-event me-1"></i>Expiration
                    </div>
                    <div class="share-section-body">
                      <input v-model="form.expiresAt" type="date" class="form-control form-control-sm" style="max-width: 200px;">
                      <div class="text-muted mt-1" style="font-size: 0.75rem;">Leave empty for no expiration.</div>
                    </div>
                  </div>

                  <!-- Enabled toggle (edit only) -->
                  <div v-if="editingShare" class="mb-3">
                    <div class="form-check">
                      <input v-model="form.enabled" class="form-check-input" type="checkbox" id="shareEnabledCheck">
                      <label class="form-check-label small" for="shareEnabledCheck">Enabled</label>
                    </div>
                  </div>

                  <!-- Form error -->
                  <div v-if="formError" class="alert alert-danger small py-1 mb-2">{{ formError }}</div>

                  <!-- Form actions -->
                  <div class="d-flex gap-2 justify-content-end border-top pt-3 mt-2">
                    <button class="btn btn-outline-secondary btn-sm" @click="cancelForm">Cancel</button>
                    <button class="btn btn-primary btn-sm" @click="submitForm" :disabled="formSubmitting">
                      <span v-if="formSubmitting" class="spinner-border spinner-border-sm me-1"></span>
                      {{ editingShare ? 'Save Changes' : 'Create Share' }}
                    </button>
                  </div>
                </div>
              </div>

              <!-- Create button -->
              <div v-if="!showCreateForm && !createdShareInfo && shares.length < maxSharesPerDefinition" class="mt-3 text-center">
                <button class="btn btn-primary btn-sm" @click="startCreateShare">
                  <i class="bi bi-plus-circle me-1"></i>Create Share Link
                </button>
              </div>
              <div v-else-if="!showCreateForm && !createdShareInfo && shares.length >= maxSharesPerDefinition" class="mt-2 text-center">
                <small class="text-muted">Maximum share limit reached ({{ maxSharesPerDefinition }}). Delete an existing share to create a new one.</small>
              </div>
            </template>

            <!-- Analytics panel (overlays the main content) -->
            <div v-if="analyticsShare" class="mt-3 border rounded p-3 bg-white">
              <div class="d-flex align-items-center justify-content-between mb-2">
                <h6 class="small text-muted text-uppercase mb-0">
                  <i class="bi bi-bar-chart me-1"></i>Analytics: {{ analyticsShare.name || 'Unnamed' }}
                </h6>
                <button class="btn btn-sm btn-outline-secondary" @click="analyticsShare = null; analyticsData = null;">
                  <i class="bi bi-x"></i>
                </button>
              </div>
              <div v-if="analyticsLoading" class="text-center py-3">
                <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
              </div>
              <div v-else-if="analyticsData">
                <div class="row g-3 mb-3">
                  <div class="col-4">
                    <div class="text-center">
                      <div class="fs-4 fw-bold text-primary">{{ analyticsViews }}</div>
                      <div class="text-muted small">Total Views</div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="text-center">
                      <div class="fs-4 fw-bold text-success">{{ analyticsUniqueIPs }}</div>
                      <div class="text-muted small">Unique IPs</div>
                    </div>
                  </div>
                  <div class="col-4">
                    <div class="text-center">
                      <div class="fs-4 fw-bold text-info">{{ analyticsDownloads }}</div>
                      <div class="text-muted small">Downloads</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>


          </div>


        </div>
      </div>
      </div>
    </template>
  </Teleport>
</template>

<script>
export default {
  name: 'ShareManagerDialog',

  props: {
    /** Whether the dialog is visible */
    show: { type: Boolean, default: false },
    /** The metadata definition ID to manage shares for */
    definitionId: { type: String, default: null },
    /** Display name of the definition */
    definitionName: { type: String, default: '' },
    /** The server root URL */
    serverRoot: { type: String, default: null },
  },

  emits: ['close'],

  data() {
    return {
      loading: false,
      error: null,
      shares: [],
      maxSharesPerDefinition: 5,

      // Create/Edit form
      showCreateForm: false,
      editingShare: null,
      form: this.emptyForm(),
      formError: null,
      formSubmitting: false,

      // Copy feedback
      copiedShareId: null,

      // Created share link display
      createdShareInfo: null,

      // Email notification
      sendEmailNotification: false,
      emailSending: false,
      emailSent: false,
      emailSentMessage: '',
      emailError: null,

      // Analytics
      analyticsShare: null,
      analyticsData: null,
      analyticsLoading: false,

      // Delete confirmation
      deleteConfirmShare: null,
      deleteInProgress: false,

      // Per-share send update email
      sendingUpdateShareId: null,
      updateEmailShareMessage: null,

      // Child users for restriction dropdown
      childUsers: [],
      childUsersLoading: false,
      childUsersError: null,
      selectedChildUser: '',
      showAddUser: false,
    };
  },

  computed: {
    createdShareLink() {
      if (!this.createdShareInfo) return '';
      const serverRoot = this.serverRoot || window.vcCommunicator?.config?.serverroot || '';
      return serverRoot + 'share/' + encodeURIComponent(this.createdShareInfo.shareId);
    },
    analyticsViews() {
      if (!this.analyticsData?.accessLog) return 0;
      return this.analyticsData.accessLog.filter(l => l.accessType === 'view').length;
    },
    analyticsUniqueIPs() {
      if (!this.analyticsData?.accessLog) return 0;
      const ips = new Set(this.analyticsData.accessLog.map(l => l.accessorIp).filter(Boolean));
      return ips.size;
    },
    analyticsDownloads() {
      if (!this.analyticsData?.accessLog) return 0;
      return this.analyticsData.accessLog.filter(l =>
        l.accessType === 'download_xlsx' || l.accessType === 'download_csv'
      ).length;
    },
    /** Shares sorted oldest first */
    sortedShares() {
      return [...this.shares].sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
    },
    /** Child users not yet added to the allowed list */
    availableChildUsers() {
      const already = new Set(this.form.allowedUsers);
      return this.childUsers.filter(u => !already.has(u.id));
    },
  },

  watch: {
    show(val) {
      if (val && this.definitionId) {
        this.loadShares();
        this.loadChildUsers();
      }
    },
    definitionId(val) {
      if (val && this.show) {
        this.loadShares();
      }
    },
  },

  methods: {
    emptyForm() {
      return {
        name: this.definitionName || '',
        shareApproach: 'link',
        password: '',
        clearPassword: false,
        allowedUsers: [],
        expiresAt: '',
        enabled: true,
        notifyOnUpdate: false,
      };
    },

    async loadChildUsers() {
      if (!window.vcCommunicator) return;
      if (this.childUsers.length) return; // already loaded
      this.childUsersLoading = true;
      this.childUsersError = null;
      try {
        const result = await window.vcCommunicator.listChildUsers();
        const users = result.filter(u => u.id && u.userName);

        // Prepend "self" (the current logged-in user) if we can identify them
        const selfId = window.vcCommunicator.sessionState?.userId;
        if (selfId && !users.some(u => u.id === selfId)) {
          users.unshift({ id: selfId, userName: selfId + ' (me)', _isSelf: true });
        }

        this.childUsers = users;
      } catch (err) {
        this.childUsersError = typeof err === 'string' ? err : (err?.message || 'Failed to load users');
      } finally {
        this.childUsersLoading = false;
      }
    },

    /** Resolve a group ID to a display username */
    resolveUserName(groupId) {
      // Try child users list first
      const child = this.childUsers.find(u => u.id === groupId);
      if (child) return child.userName;
      // Try allowedUserDetails from the editing share
      if (this.editingShare?.allowedUserDetails) {
        const detail = this.editingShare.allowedUserDetails.find(u => u.id === groupId);
        if (detail) return detail.userName;
      }
      return groupId; // fallback to raw ID
    },

    addSelectedUser() {
      if (this.selectedChildUser && !this.form.allowedUsers.includes(this.selectedChildUser)) {
        this.form.allowedUsers.push(this.selectedChildUser);
      }
      this.selectedChildUser = '';
      // Auto-close popover when no more users are available
      if (!this.availableChildUsers.length) {
        this.showAddUser = false;
      }
    },

    async loadShares() {
      if (!this.definitionId || !window.vcCommunicator) return;
      this.loading = true;
      this.error = null;
      this.createdShareInfo = null;
      try {
        const result = await window.vcCommunicator.listShares(this.definitionId);
        this.shares = Array.isArray(result) ? result : (result.shares || []);
      } catch (err) {
        this.error = typeof err === 'string' ? err : (err?.message || 'Failed to load shares');
      } finally {
        this.loading = false;
      }
    },

    startCreateShare() {
      this.editingShare = null;
      this.createdShareInfo = null;
      this.form = this.emptyForm();
      this.formError = null;
      this.showAddUser = false;
      this.showCreateForm = true;
    },

    startEditShare(share) {
      this.editingShare = share;
      this.showAddUser = false;
      const hasUsers = share.allowedUsers && share.allowedUsers.length > 0;
      this.form = {
        name: share.name || '',
        shareApproach: hasUsers ? 'users' : 'link',
        password: '',
        clearPassword: false,
        allowedUsers: share.allowedUsers ? [...share.allowedUsers] : [],
        expiresAt: share.expiresAt ? share.expiresAt.substring(0, 10) : '',
        enabled: share.enabled !== false,
        notifyOnUpdate: share.notifyOnUpdate === true,
      };
      this.formError = null;
      this.showCreateForm = true;
    },

    cancelForm() {
      this.showCreateForm = false;
      this.editingShare = null;
      this.formError = null;
    },

    async submitForm() {
      if (!window.vcCommunicator) return;
      const name = (this.form.name || '').trim();
      if (!name) {
        this.formError = 'Please enter a share name.';
        return;
      }

      // Derive allowed users and password based on the chosen approach
      const isUserShare = this.form.shareApproach === 'users';
      const allowedUsers = isUserShare
        ? this.form.allowedUsers.map(u => u.trim()).filter(Boolean)
        : [];
      const password = !isUserShare ? (this.form.password || undefined) : undefined;

      if (isUserShare && !allowedUsers.length) {
        this.formError = 'Please add at least one user for account-based sharing.';
        return;
      }

      this.formSubmitting = true;
      this.formError = null;

      try {
        if (this.editingShare) {
          // Update existing share
          await window.vcCommunicator.updateShare({
            shareId: this.editingShare.shareId,
            name,
            password: password,
            clearPassword: (!isUserShare && this.form.clearPassword) || undefined,
            allowedUsers: allowedUsers.length > 0 ? allowedUsers : [],
            expiresAt: this.form.expiresAt || undefined,
            enabled: this.form.enabled,
            notifyOnUpdate: isUserShare ? this.form.notifyOnUpdate : false,
          });

          // Show success panel for edit
          const shareId = this.editingShare.shareId;
          this.showCreateForm = false;
          this.editingShare = null;
          await this.loadShares();
          // Find the updated share from the reloaded list
          const updated = this.shares.find(s => s.shareId === shareId) || { shareId };
          this.createdShareInfo = { ...updated, _wasEdit: true, _allowedUsers: allowedUsers };
          this.resetEmailState();
          this.$nextTick(() => {
            if (this.$refs.shareLinkInput) this.$refs.shareLinkInput.select();
          });
          return;
        } else {
          // Create new share
          const created = await window.vcCommunicator.createShare({
            definitionId: this.definitionId,
            shareType: 'weather_risk_monitor',
            name,
            password: password,
            allowedUsers: allowedUsers.length > 0 ? allowedUsers : undefined,
            expiresAt: this.form.expiresAt || undefined,
            notifyOnUpdate: isUserShare ? this.form.notifyOnUpdate : false,
          });

          // Show share link UI
          if (created && created.shareId) {
            await this.loadShares();
            this.showCreateForm = false;
            this.editingShare = null;
            this.createdShareInfo = { ...created, _wasEdit: false, _allowedUsers: allowedUsers };
            this.resetEmailState();
            this.$nextTick(() => {
              if (this.$refs.shareLinkInput) {
                this.$refs.shareLinkInput.select();
              }
            });
            return;
          }
        }

        this.showCreateForm = false;
        this.editingShare = null;
        await this.loadShares();
      } catch (err) {
        this.formError = typeof err === 'string' ? err : (err?.message || 'Failed to save share');
      } finally {
        this.formSubmitting = false;
      }
    },

    async copyCreatedShareLink() {
      if (!this.createdShareInfo) return;
      await this.copyShareLink(this.createdShareInfo);
    },

    dismissCreatedShare() {
      this.createdShareInfo = null;
      this.resetEmailState();
    },

    resetEmailState() {
      this.sendEmailNotification = false;
      this.emailSending = false;
      this.emailSent = false;
      this.emailSentMessage = '';
      this.emailError = null;
    },

    async sendNotificationEmails() {
      if (!this.createdShareInfo || !window.vcCommunicator) return;
      this.emailSending = true;
      this.emailError = null;
      try {
        const result = await window.vcCommunicator.sendShareNotification({
          shareId: this.createdShareInfo.shareId,
          userIds: this.createdShareInfo._allowedUsers,
        });
        this.emailSent = true;
        const count = result?.sentCount || this.createdShareInfo._allowedUsers.length;
        this.emailSentMessage = `Notification sent to ${count} user${count !== 1 ? 's' : ''}.`;
      } catch (err) {
        this.emailError = typeof err === 'string' ? err : (err?.message || 'Failed to send notifications');
      } finally {
        this.emailSending = false;
      }
    },

    async copyShareLink(share) {
      // Build the distribution URL (server redirect URL)
      const serverRoot = this.serverRoot || window.vcCommunicator?.config?.serverroot || '';
      const url = serverRoot + 'share/' + encodeURIComponent(share.shareId);
      try {
        await navigator.clipboard.writeText(url);
      } catch {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      this.copiedShareId = share.shareId;
      setTimeout(() => { this.copiedShareId = null; }, 2000);
    },

    confirmDeleteShare(share) {
      this.deleteConfirmShare = share;
    },

    async doDeleteShare() {
      if (!this.deleteConfirmShare || !window.vcCommunicator) return;
      this.deleteInProgress = true;
      try {
        await window.vcCommunicator.deleteShare(this.deleteConfirmShare.shareId);
        this.deleteConfirmShare = null;
        await this.loadShares();
      } catch (err) {
        this.error = typeof err === 'string' ? err : (err?.message || 'Failed to delete share');
      } finally {
        this.deleteInProgress = false;
      }
    },

    /** Send data-updated notification email for a single share */
    async sendShareUpdateEmail(share) {
      if (!share || !window.vcCommunicator || !this.definitionId) return;
      this.sendingUpdateShareId = share.shareId;
      this.updateEmailShareMessage = null;
      try {
        await window.vcCommunicator.sendDataUpdateNotification(this.definitionId, share.shareId);
        this.updateEmailShareMessage = { shareId: share.shareId, text: 'Update email sent!', error: false };
        setTimeout(() => { this.updateEmailShareMessage = null; }, 3000);
      } catch (err) {
        const msg = typeof err === 'string' ? err : (err?.message || 'Failed to send');
        this.updateEmailShareMessage = { shareId: share.shareId, text: msg, error: true };
        setTimeout(() => { this.updateEmailShareMessage = null; }, 5000);
      } finally {
        this.sendingUpdateShareId = null;
      }
    },

    async showAnalytics(share) {
      this.analyticsShare = share;
      this.analyticsData = null;
      this.analyticsLoading = true;
      try {
        this.analyticsData = await window.vcCommunicator.getShareAnalytics(share.shareId);
      } catch (err) {
        this.error = typeof err === 'string' ? err : (err?.message || 'Failed to load analytics');
        this.analyticsShare = null;
      } finally {
        this.analyticsLoading = false;
      }
    },

    formatDate(dateStr) {
      if (!dateStr) return '—';
      try {
        const d = new Date(dateStr);
        return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
      } catch {
        return dateStr;
      }
    },

    isExpired(share) {
      if (!share.expiresAt) return false;
      return new Date(share.expiresAt) < new Date();
    },
  },
};
</script>

<style scoped>
.fwt-password-input {
  -webkit-text-security: disc;
}
.add-user-popover {
  background: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 8px 10px;
}

/* Share form container */
.share-form-container {
  border: 1px solid #dee2e6;
  border-radius: 0.5rem;
  overflow: hidden;
  background: #fff;
}
.share-form-header {
  background: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
  padding: 10px 16px;
}
.share-form-body {
  padding: 16px;
}

/* Approach radio cards */
.share-approach-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.share-approach-card {
  display: block;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  padding: 10px 12px;
  cursor: pointer;
  background: #fff;
  transition: border-color 0.15s, background-color 0.15s;
}
.share-approach-card:hover {
  background: #f0f6ff;
  border-color: #aecbfa;
}
.share-approach-card--active {
  border-color: #0d6efd;
  background: #f0f6ff;
  box-shadow: 0 0 0 1px #0d6efd;
}

/* Sub-sections within form */
.share-section {
  border: 1px solid #e9ecef;
  border-radius: 0.375rem;
  overflow: hidden;
}
.share-section-header {
  background: #f8f9fa;
  padding: 6px 12px;
  font-size: 0.8rem;
  font-weight: 600;
  color: #495057;
  border-bottom: 1px solid #e9ecef;
}
.share-section-body {
  padding: 10px 12px;
}
</style>
